import type {
  ConversationSessionState,
  OrchestratorMetadata,
  RespondentStreamEvent,
} from "@kushya/shared";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

// Re-export shared types for consumers
export type { ConversationSessionState, OrchestratorMetadata };

// Use RespondentStreamEvent from shared, aliased as StreamEvent for backward compatibility
export type StreamEvent = RespondentStreamEvent;

/**
 * Input parameters for sending a message
 */
export interface SendMessageInput {
  projectId: string;
  formId: string;
  version: number;
  message: string;
  responseId?: string;
  sessionToken?: string;
}

/**
 * Parse SSE data line into typed event
 */
function parseSSEData(data: string): StreamEvent | null {
  if (!data.trim()) {
    return null;
  }

  try {
    return JSON.parse(data) as StreamEvent;
  } catch {
    console.warn("Failed to parse SSE data:", data);
    return null;
  }
}

function buildErrorMessage(payload: any, fallback: string): string {
  const code = payload?.error?.code;
  const message = payload?.error?.message;

  if (typeof code === "string" && typeof message === "string") {
    return `${code}: ${message}`;
  }

  if (typeof message === "string") {
    return message;
  }

  if (typeof payload?.error === "string") {
    return payload.error;
  }

  return fallback;
}

/**
 * Async generator that streams conversation messages from the backend.
 * Yields events as they arrive: metadata, tokens, and complete.
 */
export async function* streamConversationMessage(
  input: SendMessageInput,
): AsyncGenerator<StreamEvent> {
  const { projectId, formId, version, message, responseId, sessionToken } =
    input;

  const url = `${API_BASE_URL}/forms/${encodeURIComponent(projectId)}/${encodeURIComponent(formId)}/${version}/conversation/send-message`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };

  // Include session token if available
  if (sessionToken) {
    headers["X-Session-Token"] = sessionToken;
  }

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify({
      message,
      responseId,
      metadata: {
        userAgent: navigator.userAgent,
        referrer: document.referrer || undefined,
      },
    }),
  });

  if (!response.ok) {
    let errorMessage = `API error: ${response.status}`;

    try {
      const errorBody = await response.json();
      errorMessage = buildErrorMessage(errorBody, errorMessage);
    } catch {
      // Use default error message
    }

    throw new Error(errorMessage);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body available");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Process any remaining data in buffer
        if (buffer.trim()) {
          const lines = buffer.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const event = parseSSEData(line.slice(6));
              if (event) {
                yield event;
              }
            }
          }
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const event = parseSSEData(line.slice(6));
          if (event) {
            yield event;
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export interface FetchConversationSessionInput {
  projectId: string;
  formId: string;
  version: number;
  sessionToken: string;
}

export async function fetchConversationSession(
  input: FetchConversationSessionInput,
): Promise<ConversationSessionState> {
  const { projectId, formId, version, sessionToken } = input;

  const url = `${API_BASE_URL}/forms/${encodeURIComponent(projectId)}/${encodeURIComponent(formId)}/${version}/conversation/session`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-Session-Token": sessionToken,
    },
  });

  if (!response.ok) {
    let errorMessage = `API error: ${response.status}`;

    try {
      const errorBody = await response.json();
      errorMessage = buildErrorMessage(errorBody, errorMessage);
    } catch {
      // Use default error message
    }

    throw new Error(errorMessage);
  }

  const body = await response.json();
  if (!body?.success || !body?.data) {
    throw new Error("Failed to restore conversation session");
  }

  return body.data as ConversationSessionState;
}
